import * as React from 'react';
import Box from '@mui/material/Box';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useQuery } from 'react-query';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import { useWorkaroundBodyBackground } from '../../../../../../shared/hooks/workaroundBodyBackground';
import { Translate } from '../../../../../../shared/LanguageService';
import { SendBookingRequestModal } from './SendBookingRequestModal';
import { CreateOrderResult } from '../../../../../orders/OrdersApi';
import { getRedirectPathToOrder } from '../../../../../orders/utils/redirectOrderFlow';
import {
    getBookingInformationFromBetweenNavigation,
    OrderInfoFromBookingFlow
} from '../adapters/dataSharingBetweenNavigation';
import { useTelemetryContext } from '../../../../../../telemetry/telemetryContext';
import { AuthenticatedUser } from '../../../../../../shared/types';
import { getUserInfo } from '../../../../../../telemetry/utils';
import { useCreateOrderMutation } from '../queries/createOrderMutation';
import { Heading } from '../../shared/view/Heading';
import { AvailablePhotographer, CustomerBookingResult } from '../../shared/types';
import { CustomerPhotographerBookingApi } from '../../shared/queries/CustomerPhotographerBookingApi';
import { CustomerBookingMobileSkeleton } from '../../shared/view/CustomerBookingMobileSkeleton';
import { useSendBookingRequestMutation } from '../queries/bookingRequestMutation';
import {
    BookingMethod,
    BookingMethodSection
} from '../../shared/timeCentric/view/BookingMethodSection';
import { TimeslotCentric } from '../../shared/timeCentric/view/TimeslotCentric';
import { PhotographerCentric } from '../../shared/photographerCentric/view/PhotographerCentric';
import {
    BookableDays,
    getNextWorkday,
    getStartBookingDate
} from '../../shared/bookingDateSelect/state/dateHelper';
import {
    noBookableDays,
    useBookableDaysFrom
} from '../../shared/bookingDateSelect/state/useBookableDaysFrom';
import {
    clearPersistedBookingMethod,
    manageSavedBookingMethod,
    usePersistBookingMethodSelection
} from '../../shared/state/bookingMethodUtil';
import {clearPersistedPhotographer} from "../../shared/photographerCentric/state/photographerUtils";

type CustomerBookingCalendarMobileProps = {
    availablePhotographers: AvailablePhotographer[];
    displayingBookingDate: Date;
    earliestBookableDate?: Date | null;
    isCustomerAllowedToRequestBooking: boolean;
    onChangeDisplayingBookingDate: (dateTime: Date) => void;
    onSendBookingRequest: (preferredDate: Date | null, notes: string) => Promise<any>;
    onBook: (photographerId: number, timeslot: string) => void;
    user: AuthenticatedUser;
};

export function CustomerBookingCalendarMobile({
    availablePhotographers,
    displayingBookingDate,
    earliestBookableDate = null,
    isCustomerAllowedToRequestBooking,
    onChangeDisplayingBookingDate,
    onSendBookingRequest,
    user,
    onBook
}: CustomerBookingCalendarMobileProps) {
    useWorkaroundBodyBackground('rgba(250, 250, 250, 1)');

    let [showHints, setShowHints] = React.useState(false);
    let [bookingMethod, setBookingMethod] = React.useState(BookingMethod.TimeCentric);
    let [showSendRequestModal, setShowSendRequestModal] = React.useState(false);

    let { logger } = useTelemetryContext();

    usePersistBookingMethodSelection(
        bookingMethod,
        (newBookingMethod: BookingMethod) => {
            if (newBookingMethod !== bookingMethod) {
                setBookingMethod(newBookingMethod);
            }
        },
        [bookingMethod]
    );

    function handleChangeBookingMethod(newBookingMethod: BookingMethod) {
        manageSavedBookingMethod(newBookingMethod);

        setBookingMethod(newBookingMethod);
    }

    function handleBookFactory(
        planOrderId: number | null,
        photographerId: number | null
    ): (timeslot: string) => void {
        return function handleBook(timeslot: string) {
            if (photographerId) {
                clearPersistedBookingMethod();
                clearPersistedPhotographer();
                onBook(photographerId, timeslot);
            } else {
                logger.logException('Should not be possible to book without a photographer!');
            }
        };
    }

    return (
        <Box
            component="section"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%'
            }}
        >
            <Heading>
                {Translate('se360/photographerbooking/customer/calendar/title')}
                <IconButton
                    onClick={() => {
                        setShowHints((prev) => !prev);
                    }}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    <InfoIcon sx={{ color: 'secondary.dark' }} />
                </IconButton>
            </Heading>
            {isCustomerAllowedToRequestBooking === true ? (
                <Button
                    sx={{
                        padding: '20px 0',
                        textTransform: 'capitalize',
                        textDecoration: 'underline',
                        fontSize: '1.6rem'
                    }}
                    onClick={() => {
                        setShowSendRequestModal(true);
                    }}
                >
                    {Translate('se360/photographerbooking/page/button/let-se360-book')}
                </Button>
            ) : null}
            <BookingMethodSection
                showHint={showHints}
                onChange={handleChangeBookingMethod}
                value={bookingMethod}
            />
            {bookingMethod == BookingMethod.TimeCentric ? (
                <TimeslotCentric
                    allAvailablePhotographers={availablePhotographers}
                    displayingBookingDate={displayingBookingDate}
                    handleBookFactory={handleBookFactory}
                    onChangeDisplayingBookingDate={onChangeDisplayingBookingDate}
                    planOrderId={null}
                    showHints={showHints}
                    earliestBookableDate={earliestBookableDate}
                />
            ) : (
                <PhotographerCentric
                    availablePhotographers={availablePhotographers}
                    displayingBookingDate={displayingBookingDate}
                    handleBookFactory={handleBookFactory}
                    onChangeDisplayingBookingDate={onChangeDisplayingBookingDate}
                    planOrderId={null}
                    showHints={showHints}
                    earliestBookableDate={earliestBookableDate}
                />
            )}
            <SendBookingRequestModal
                labels={{
                    title: Translate('se360/dusk-photographer-booking/request-modal-title'),
                    text: Translate('se360/dusk-photographer-booking/request-modal-text'),
                    date: Translate('se360/dusk-photographer-booking/request-modal-date'),
                    info: Translate('se360/dusk-photographer-booking/request-modal-info'),
                    sendButton: Translate(
                        'se360/photographerbooking/customer/calendar/button/send'
                    ),
                    cancelButton: Translate('cancel')
                }}
                show={showSendRequestModal}
                onClose={() => {
                    setShowSendRequestModal(false);
                }}
                onSend={async (preferredDate: Date | null, notes: string) => {
                    try {
                        setShowSendRequestModal(false);
                        await onSendBookingRequest(preferredDate, notes);
                    } catch (error) {
                        let additionalInfo = {
                            origin: 'CustomerBookingCalendarMobile.SendBookingRequestModal.onSend',
                            userInfo: getUserInfo(user)
                        };
                        logger.logException(error, additionalInfo);
                    }
                }}
            />
        </Box>
    );
}

export let CustomerBookingMobileQueryKey = 'CustomerBookingMobileQuery';

type ExternalProps = {
    user: AuthenticatedUser;
};

function readBookingInformation(): OrderInfoFromBookingFlow | null {
    try {
        return getBookingInformationFromBetweenNavigation();
    } catch (error) {
        return null;
    }
}

export default withRouter(function CustomerBookingCalendarMobileDataLayer({
    history,
    user
}: ExternalProps & RouteComponentProps) {
    let [defaultBookingDate] = React.useState(() => getNextWorkday(new Date(Date.now())));
    let [wantedBookingDate, setWantedBookingDate] = React.useState<Date | null>(null);
    let [bookableDays, setBookableDays] = React.useState<BookableDays>(noBookableDays);
    let { logger } = useTelemetryContext();

    let bookingInformation = readBookingInformation();
    let hasBookingInformation = bookingInformation !== null;
    let orderInformation = hasBookingInformation ? bookingInformation.orderInformation : null;

    let displayingBookingDate = getStartBookingDate(
        wantedBookingDate,
        defaultBookingDate,
        bookableDays
    );

    let { data, isError, error, isLoading } = useQuery<CustomerBookingResult>(
        [CustomerBookingMobileQueryKey, displayingBookingDate],
        () =>
            CustomerPhotographerBookingApi.getPhotographersForNewCustomerBooking(
                bookingInformation.address,
                bookingInformation.selectedProductIds,
                Number(bookingInformation.brokerFirmId),
                displayingBookingDate
            ),
        {
            enabled: hasBookingInformation,
            onError: (error: unknown) => {
                let additionalInfo = {
                    origin: 'CustomerBookingCalendarMobile.getPhotographersForNewCustomerBooking',
                    userInfo: getUserInfo(user)
                };

                logger.logException(error, additionalInfo);
                history.push('/orders/new');
            }
        }
    );
    let isTakingOverBookableDays = useBookableDaysFrom(data, bookableDays, setBookableDays);

    let sendBookingRequestMutation = useSendBookingRequestMutation(
        orderInformation,
        function handleSuccess({
            photoOrderId,
            planOrderId,
            digitalStylingOrderId
        }: CreateOrderResult) {
            let path = getRedirectPathToOrder({
                photoOrderId,
                planOrderId,
                digitalStylingOrderId
            });

            history.push(path);
        }
    );

    let createOrderMutation = useCreateOrderMutation(
        orderInformation,
        function handleSuccess({
            photoOrderId,
            planOrderId,
            digitalStylingOrderId
        }: CreateOrderResult) {
            let path = getRedirectPathToOrder({
                photoOrderId,
                planOrderId,
                digitalStylingOrderId
            });

            history.push(path);
        }
    );

    function handleCreateOrderRequest(
        photographerId: number,
        timeslot: string
    ): Promise<CreateOrderResult> {
        return createOrderMutation.mutateAsync({ timeslot, photographerId });
    }

    function handleSendBookingRequest(
        preferredDate: Date,
        notes: string
    ): Promise<CreateOrderResult> {
        return sendBookingRequestMutation.mutateAsync({ preferredDate, notes });
    }

    if (!hasBookingInformation) {
        history.push('/orders/new');
        return null;
    }

    if (isLoading || isTakingOverBookableDays) {
        return <CustomerBookingMobileSkeleton />;
    }

    if (isError) {
        const additionalInfo = {
            origin: 'CustomerBookingCalendarMobile.getPhotographersForNewCustomerBooking',
            userInfo: getUserInfo(user)
        };
        logger.logException(error, additionalInfo);
    }

    if (!data) {
        history.push('/orders/new');
        return null;
    }

    let availablePhotographers = data.availablePhotographers ? data.availablePhotographers : [];

    return (
        <CustomerBookingCalendarMobile
            availablePhotographers={availablePhotographers}
            displayingBookingDate={displayingBookingDate}
            earliestBookableDate={bookableDays.earliestBookableDate}
            onChangeDisplayingBookingDate={setWantedBookingDate}
            onSendBookingRequest={handleSendBookingRequest}
            onBook={handleCreateOrderRequest}
            user={user}
            isCustomerAllowedToRequestBooking={data.isCustomerAllowedToRequestBooking}
        />
    );
});
